import { useEffect, useMemo } from 'react';
import { Group } from '@mantine/core';
import { FileExport } from '../CompressionAndExportOperation';
import { CompressionAndExport } from '@/app/workflows/actions/interfaces';
import { CustomisedTextureFormatSelection } from '../controls/CustomisedTextureFormatSelection';
import { useFormContext } from '../../../contexts/formContext';
import { OptionalSetting } from '../../../components/OptionalSetting';
import { FileFormatSelect } from '../controls/FileFormatSelect';
import { CompressionSelect } from '../controls/CompressionSelect';
import { TextureFormatSelect } from '../controls/TextureFormatSelect';
import { isCompressible } from '../utils/isCompressible';
import { getAllowedTextureFormats } from '../utils/getAllowedTextureFormats';

interface SingleFileSettingsProps {
  title: string;
  onChanged: (isEnabled: boolean) => void;
  isEnabled: boolean;
  fileNumber: number;
  template: FileExport;
  canChange: boolean;
}

export function SingleFileSettings({
  title,
  onChanged,
  isEnabled,
  fileNumber,
  template,
  canChange,
}: SingleFileSettingsProps) {
  const form = useFormContext();
  const formPath = `compressionAndExport.fileExports.${fileNumber}`;
  const fileExportValue = form.values.compressionAndExport.fileExports[fileNumber] ?? template;

  const isCustomSelection = useMemo(
    () =>
      Object.values(
        Object.values(fileExportValue.textureFormat ?? {}).reduce((acc, next) => ({ ...acc, [next]: 1 }), {})
      ).length > 1,
    [fileExportValue.textureFormat]
  );

  const compressible = useMemo(() => isCompressible(fileExportValue.fileType), [fileExportValue.fileType]);

  const allowedTextureFileFormats = useMemo(
    () => getAllowedTextureFormats(fileExportValue.fileType),
    [fileExportValue.fileType]
  );

  const globalValue = isCustomSelection ? 'custom' : fileExportValue.textureFormat?.orm;

  const onGlobalValueChanged = (option: string | null) => {
    if (!option) {
      return;
    }
    form.setFieldValue(`${formPath}.textureFormat`, {
      orm: option,
      baseColor: option,
      emissive: option,
      normal: option,
    } as CompressionAndExport['fileExports'][number]['textureFormat']);
  };

  useEffect(() => {
    if (!fileExportValue.textureFormat) {
      return;
    }
    Object.entries(fileExportValue.textureFormat).forEach(([key, value]) => {
      if (!allowedTextureFileFormats.find((allowedTextureFormat) => allowedTextureFormat.value === value)) {
        form.setFieldValue(`${formPath}.textureFormat.${key}`, allowedTextureFileFormats[0].value);
      }
    });
  }, [formPath, form.setFieldValue, fileExportValue.textureFormat, allowedTextureFileFormats]);

  return (
    <OptionalSetting title={title} onChanged={onChanged} isEnabled={isEnabled} isReadOnly={!canChange}>
      <FileFormatSelect {...form.getInputProps(`${formPath}.fileType`)} />
      <CompressionSelect disabled={!compressible} {...form.getInputProps(`${formPath}.meshCompressionMethod`)} />
      <Group align='flex-end' gap='sm'>
        <TextureFormatSelect options={allowedTextureFileFormats} value={globalValue} onChange={onGlobalValueChanged} />
        <CustomisedTextureFormatSelection
          allowedTextureFileFormats={allowedTextureFileFormats}
          fileNumber={fileNumber}
        />
      </Group>
    </OptionalSetting>
  );
}
